import { LocationType } from '../SearchLocation.types'

/**
 * Configuration interface for SearchLocation component
 * All values can be overridden via props
 */
export interface SearchLocationConfig {
  /** Minimum characters before triggering search */
  minSearchLength: number
  /** Maximum number of locations to display */
  maxLocations: number
  /** Debounce delay in milliseconds */
  debounceDelay: number
  /** Placeholder text for input */
  placeholder: string
  /** Text shown when no results found */
  noResultsText: string
  /** Text shown while loading */
  loadingText: string
  /** Default location types to search */
  defaultSearchTypes: LocationType[]
  /** Display labels for location types */
  typeLabels: Record<LocationType, string>
  /** Colors for location type tags */
  typeColors: Record<LocationType, string>
}

/**
 * Default configuration for SearchLocation component
 * Matches existing hardcoded values for backward compatibility
 */
export const defaultSearchConfig: SearchLocationConfig = {
  minSearchLength: 3,
  maxLocations: 20,
  debounceDelay: 300,
  placeholder: 'Search for an address, city, or region...',
  noResultsText: 'No locations found',
  loadingText: 'Loading...',
  defaultSearchTypes: [
    'address',
    'municipality',
    'land',
    'egid',
    'egrid',
    'npa',
  ],
  typeLabels: {
    address: 'Address',
    municipality: 'Municipality',
    land: 'Land',
    egid: 'EGID',
    egrid: 'EGRID',
    npa: 'Postal Code',
  },
  typeColors: {
    address: '#00ade6',
    municipality: '#10b981',
    land: '#6366f1',
    egid: '#f59e0b',
    egrid: '#f59e0b',
    npa: '#ef4444',
  },
}

/**
 * Merge user config with defaults
 * @param userConfig - Partial config from props
 * @returns Complete configuration object
 */
export const mergeConfig = (
  userConfig?: Partial<SearchLocationConfig>
): SearchLocationConfig => {
  if (!userConfig) {
    return defaultSearchConfig
  }

  return {
    ...defaultSearchConfig,
    ...userConfig,
    // Deep merge objects
    typeLabels: {
      ...defaultSearchConfig.typeLabels,
      ...userConfig.typeLabels,
    },
    typeColors: {
      ...defaultSearchConfig.typeColors,
      ...userConfig.typeColors,
    },
  }
}
